import { useTu } from "@src/appV2/i18n/useTu";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { alpha, Box, Stack, type Theme } from "@mui/material";
import { CopyableText } from "@src/appV2/CopyableText/CopyableText";
import { formatPhoneNumberWithCode } from "@src/appV2/lib/utils/formatPhoneNumber";
import { Button } from "@src/appV2/redesign/components/Button";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { type UseModalState } from "@src/lib/ui-react";
import { type ReactNode } from "react";
import { type ContactConflict } from "../utils/parseContactConflict";

interface Props {
  modalState: UseModalState;
  identifierType: "email" | "phone_number";
  conflict: ContactConflict;
}

// The conflicting worker is opened by re-running the admin pseudo-login flow
// (`/calendar` consumes `userId` + `drawer`, calls `pseudo`, then redirects to
// `/enter` which lands on `/agent/dashboard` with the worker drawer reopened).
// The legacy `/workplace/workers/:workerId` route is gated by `isWorkplace`, so
// it does not match for an admin pseudo'd as a worker and the navigation
// silently falls back to the current page.
function buildConflictWorkerHref(workerId: string): string {
  return `/agent/dashboard?userId=${encodeURIComponent(workerId)}&drawer=Agent`;
}

// Backend stores phones with the country code prefixed (e.g. "+13609439624").
// `formatUsPhoneNumber` truncates to the first 10 digits, which would drop the
// last digit when the leading "1" is included. Strip a leading US country code
// before formatting so the displayed number is always intact.
function formatConflictPhone(rawPhone: string): string {
  const digits = rawPhone.replace(/\D/g, "");
  const localDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return formatPhoneNumberWithCode({ phoneNumber: localDigits });
}

export function WorkerContactConflictDialog({ modalState, identifierType, conflict }: Props) {
  const { tu } = useTu("workerProfile");

  const isPhone = identifierType === "phone_number";
  const conflictHref = buildConflictWorkerHref(conflict.cbhUserId);
  const rawIdentifierValue = isPhone ? conflict.phone : conflict.email;

  const formattedIdentifierValue =
    isPhone && rawIdentifierValue ? formatConflictPhone(rawIdentifierValue) : rawIdentifierValue;

  const title = isPhone ? tu("Phone number already assigned") : tu("Email already assigned");
  const identifierUsedMessage = isPhone
    ? tu("This phone number is already used by another worker.")
    : tu("This email is already used by another worker.");
  const updateIdentifierMessage = isPhone
    ? tu(
        "Open the conflicting worker profile and remove or update the phone number before trying again."
      )
    : tu("Open the conflicting worker profile and remove or update the email before trying again.");
  const cardLabel = isPhone ? tu("Conflicting phone number") : tu("Conflicting email");
  const copyIdentifierLabel = isPhone ? tu("Copy phone number") : tu("Copy email");

  return (
    <BerlinThemeProvider>
      <ResponsiveDialog
        modalState={modalState}
        width={600}
        headerContent={
          <ConflictDialogHeader
            title={title}
            iconNode={<Icon type="warning" size="small" />}
            closeLabel={tu("Close dialog")}
            onClose={modalState.closeModal}
          />
        }
        footerContent={
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ paddingX: 5, paddingY: 4 }}
          >
            <Button variant="outlined" onClick={modalState.closeModal}>
              {tu("Cancel")}
            </Button>
            <Button
              aria-label={tu("Open conflicting worker profile")}
              href={conflictHref}
              startIconType="hyperlink"
              variant="primary"
            >
              {tu("Open conflicting worker profile")}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Text variant="body1">{identifierUsedMessage}</Text>
            <Text variant="body1">{updateIdentifierMessage}</Text>
          </Stack>

          {rawIdentifierValue !== undefined && (
            <InfoCard
              tone="danger"
              icon={isPhone ? <PhoneOutlinedIcon /> : <EmailOutlinedIcon />}
              label={cardLabel}
              value={formattedIdentifierValue ?? rawIdentifierValue}
              copyValue={rawIdentifierValue}
              copyLabel={copyIdentifierLabel}
            />
          )}

          <InfoCard
            valueMonospace
            tone="info"
            icon={<PersonOutlineIcon />}
            label={tu("Assigned to another worker")}
            sublabel={tu("Worker ID")}
            value={conflict.cbhUserId}
            copyValue={conflict.cbhUserId}
            copyLabel={tu("Copy Worker ID")}
          />
        </Stack>
      </ResponsiveDialog>
    </BerlinThemeProvider>
  );
}

interface ConflictDialogHeaderProps {
  title: string;
  iconNode: ReactNode;
  closeLabel: string;
  onClose: () => void;
}

function ConflictDialogHeader({ title, iconNode, closeLabel, onClose }: ConflictDialogHeaderProps) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(3),
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(4),
        borderBottom: `1px solid ${theme.palette.border?.base}`,
      })}
    >
      <IconBadge tone="danger">{iconNode}</IconBadge>
      <Text bold sx={{ flex: 1, minWidth: 0, whiteSpace: "normal" }}>
        {title}
      </Text>
      <IconButton
        circular
        aria-label={closeLabel}
        iconType="close"
        size="small"
        variant="outlined"
        onClick={onClose}
      />
    </Box>
  );
}

type Tone = "danger" | "info";

// Tone tokens map to the Berlin palette: danger uses `base.danger` (the brand
// red used for error states), info uses `base.muted` (a neutral 60% black).
// Both are existing design-system tokens; do not introduce new color
// constants here.
function getToneColor(theme: Theme, tone: Tone): string {
  if (tone === "danger") {
    return theme.palette.base?.danger ?? theme.palette.error.main;
  }
  return theme.palette.base?.muted ?? theme.palette.text.secondary;
}

function IconBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: getToneColor(theme, tone),
        backgroundColor: alpha(getToneColor(theme, tone), 0.12),
      })}
    >
      {children}
    </Box>
  );
}

interface InfoCardProps {
  tone: Tone;
  icon: ReactNode;
  label: string;
  sublabel?: string;
  value: string;
  valueMonospace?: boolean;
  copyValue: string;
  copyLabel: string;
}

function InfoCard({
  tone,
  icon,
  label,
  sublabel,
  value,
  valueMonospace = false,
  copyValue,
  copyLabel,
}: InfoCardProps) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(3),
        paddingX: theme.spacing(4),
        paddingY: theme.spacing(3),
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.spacing(2),
      })}
    >
      <IconBadge tone={tone}>{icon}</IconBadge>
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Text bold variant="body2">
          {label}
        </Text>
        {sublabel && (
          <Text variant="caption" color="text.secondary">
            {sublabel}
          </Text>
        )}
        <Text
          variant="body1"
          sx={{
            wordBreak: "break-all",
            fontFamily: valueMonospace ? "monospace" : undefined,
          }}
        >
          {value}
        </Text>
      </Stack>
      {/* Use the same `CopyableText` the worker drawer uses, with `showText`
         disabled so only the copy icon-button renders here (the value is
         already shown in the card body above). The accessible name is set on
         the IconButton itself via `copyButtonAriaLabel` rather than on this
         wrapping `Box` (a `div`), since screen readers don't announce
         non-interactive elements. */}
      <Box sx={{ flexShrink: 0 }}>
        <CopyableText showText={false} value={copyValue} copyButtonAriaLabel={copyLabel} />
      </Box>
    </Box>
  );
}
